import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AuthorSeed {
  id: string;
  name: string;
  name_normalized: string;
  primary_genre: string | null;
  priority_tier: number;
  max_works: number;
  allow_backlist: boolean;
  status: 'active' | 'paused';
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_imported_at: string | null;
  books_imported: number;
}

export interface SeedUpload {
  id: string;
  filename: string;
  uploaded_by: string;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  error_count: number;
  errors: { row: number; error: string }[];
  created_at: string;
}

interface ListParams {
  status?: 'active' | 'paused';
  genre?: string;
  tier?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

interface UploadResult {
  success: boolean;
  inserted: number;
  updated: number;
  skipped: number;
  errors: { row: number; error: string }[];
}

interface StatsResult {
  total: number;
  byStatus: { active: number; paused: number };
  byTier: { 1: number; 2: number; 3: number };
  recentUploads: SeedUpload[];
}

export function useAuthorSeeds() {
  const [seeds, setSeeds] = useState<AuthorSeed[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsResult | null>(null);
  const { toast } = useToast();

  const callFunction = async (action: string, params: Record<string, unknown> = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await supabase.functions.invoke('upload-author-seeds', {
      body: { action, ...params },
    });

    if (response.error) throw response.error;
    return response.data;
  };

  const fetchSeeds = useCallback(async (params: ListParams = {}) => {
    setLoading(true);
    try {
      const data = await callFunction('list', params as unknown as Record<string, unknown>);
      setSeeds(data.seeds || []);
      setTotal(data.total || 0);
      setGenres(data.genres || []);
    } catch (error) {
      console.error('Error fetching seeds:', error);
      toast({
        title: 'Error',
        description: 'Failed to load author seeds',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const uploadCSV = useCallback(async (csvContent: string, filename: string): Promise<UploadResult> => {
    setLoading(true);
    try {
      const data = await callFunction('upload', { csvContent, filename });
      toast({
        title: 'Upload Complete',
        description: `Inserted: ${data.inserted}, Updated: ${data.updated}, Errors: ${data.errors?.length || 0}`,
      });
      return data;
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const updateSeed = useCallback(async (id: string, updates: Partial<AuthorSeed>) => {
    try {
      await callFunction('update', { id, ...updates });
      setSeeds(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
      toast({ title: 'Updated', description: 'Author seed updated successfully' });
    } catch (error) {
      console.error('Error updating seed:', error);
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  const deleteSeed = useCallback(async (id: string, hard = false) => {
    try {
      await callFunction('delete', { id, hard });
      if (hard) {
        setSeeds(prev => prev.filter(s => s.id !== id));
      } else {
        setSeeds(prev => prev.map(s => s.id === id ? { ...s, status: 'paused' as const } : s));
      }
      toast({ title: hard ? 'Deleted' : 'Paused', description: 'Author seed removed' });
    } catch (error) {
      console.error('Error deleting seed:', error);
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  const addSeed = useCallback(async (seed: Omit<AuthorSeed, 'id' | 'name_normalized' | 'created_at' | 'updated_at' | 'created_by' | 'last_imported_at' | 'books_imported'>) => {
    try {
      const data = await callFunction('add', seed);
      if (data.seed) {
        setSeeds(prev => [data.seed, ...prev]);
      }
      toast({ title: 'Added', description: 'Author seed added successfully' });
      return data.seed;
    } catch (error) {
      console.error('Error adding seed:', error);
      toast({
        title: 'Add Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await callFunction('stats');
      setStats(data);
      return data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }, []);

  return {
    seeds,
    genres,
    total,
    loading,
    stats,
    fetchSeeds,
    uploadCSV,
    updateSeed,
    deleteSeed,
    addSeed,
    fetchStats,
  };
}
